import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { getCustomIdContext, CUSTOM_ID_SCHEMA } from './customIdContext';

let snowplowModule: any = null;
let tracker: any = null;
let rawTracker: any = null;
// The browser tracker cannot be removed once created (no removeTracker in
// @snowplow/browser-tracker v3), so the instance is kept across re-inits and
// survives resetForRetry().
let webTrackerInstance: any = null;
let isInitialized = false;
let initAttemptCount = 0;
let usingMockTracker = false;
let initializedAppId: string | null = null;
let initializedCountryCode: string | null = null;

const MAX_INIT_ATTEMPTS = 3;

const SNOWPLOW_LOGGING_ENABLED = __DEV__ || process.env.EXPO_PUBLIC_SNOWPLOW_LOGGING === 'true';

function logSnowplowEvent(methodName: string, event: any, contexts?: any, result?: any, error?: any) {
  if (!SNOWPLOW_LOGGING_ENABLED) return;

  const timestamp = new Date().toISOString();
  const logPrefix = `[SNOWPLOW_TRACKING][${Platform.OS}][${timestamp}]`;

  console.log(`${logPrefix} ========== TRACKING REQUEST ==========`);
  console.log(`${logPrefix} Method: ${methodName}`);
  console.log(`${logPrefix} AppId: ${initializedAppId || 'NOT_SET'}`);
  console.log(`${logPrefix} CountryCode: ${initializedCountryCode || 'NOT_SET'}`);

  if (event) {
    console.log(`${logPrefix} Event Schema: ${event.schema || 'N/A'}`);
    console.log(`${logPrefix} Event Data: ${JSON.stringify(event.data || event, null, 2)}`);
  }

  if (contexts && Array.isArray(contexts) && contexts.length > 0) {
    console.log(`${logPrefix} Contexts (${contexts.length}):`);
    contexts.forEach((ctx: any, index: number) => {
      console.log(`${logPrefix}   [${index}] Schema: ${ctx.schema || 'N/A'}`);
      console.log(`${logPrefix}   [${index}] Data: ${JSON.stringify(ctx.data || ctx, null, 2)}`);
    });
  }

  if (error) {
    console.log(`${logPrefix} ERROR: ${error.message || error}`);
  } else if (result !== undefined) {
    console.log(`${logPrefix} Result: SUCCESS`);
  }

  console.log(`${logPrefix} =======================================`);
}

function injectGlobalContexts(contexts?: any): any[] {
  const existing = Array.isArray(contexts) ? contexts : [];
  // Queued events replay with the custom_id context snapshotted at fire time
  // (web.ts) — don't stack the current one on top of it.
  if (existing.some((c: any) => c?.schema === CUSTOM_ID_SCHEMA)) {
    return existing;
  }
  const customIdCtx = getCustomIdContext();
  if (customIdCtx) {
    return [...existing, customIdCtx];
  }
  return existing;
}

function wrapTrackerWithLogging(originalTracker: any): any {
  if (!originalTracker) return originalTracker;

  return {
    ...originalTracker,
    trackSelfDescribingEvent: (event: any, contexts?: any) => {
      const enrichedContexts = injectGlobalContexts(contexts);
      logSnowplowEvent('trackSelfDescribingEvent', event, enrichedContexts);
      try {
        const result = originalTracker.trackSelfDescribingEvent(event, enrichedContexts.length > 0 ? enrichedContexts : undefined);
        if (result && typeof result.then === 'function') {
          return result.then((res: any) => {
            logSnowplowEvent('trackSelfDescribingEvent', event, enrichedContexts, res);
            return res;
          }).catch((err: any) => {
            logSnowplowEvent('trackSelfDescribingEvent', event, enrichedContexts, undefined, err);
            throw err;
          });
        }
        return result;
      } catch (err) {
        logSnowplowEvent('trackSelfDescribingEvent', event, enrichedContexts, undefined, err);
        throw err;
      }
    },
    setUserId: (userId: string | null) => {
      console.log(`[SNOWPLOW_TRACKING][${Platform.OS}] setUserId: ${userId || 'null'}`);
      return originalTracker.setUserId(userId);
    },
    ...(typeof originalTracker.trackPageView === 'function' ? {
      trackPageView: (pageTitle: string, contexts?: any) => {
        const enrichedContexts = injectGlobalContexts(contexts);
        logSnowplowEvent('trackPageView (built-in pv event)', { data: { title: pageTitle } }, enrichedContexts);
        try {
          return originalTracker.trackPageView(pageTitle, enrichedContexts.length > 0 ? enrichedContexts : undefined);
        } catch (err) {
          logSnowplowEvent('trackPageView', { data: { title: pageTitle } }, enrichedContexts, undefined, err);
          throw err;
        }
      },
    } : {}),
  };
}

function buildSnowplowUserAgent(): string {
  const appVersion = Constants.expoConfig?.version || '1.0.0';
  const os = Platform.OS === 'ios' ? 'iOS' : Platform.OS === 'android' ? 'Android' : Platform.OS;
  const osVersion = typeof Platform.Version === 'number' ? String(Platform.Version) : (Platform.Version || 'unknown');
  return `Hekaya/${appVersion} (${os}; ${osVersion})`;
}

function getMockTracker() {
  const mockTracker = {
    trackSelfDescribingEvent: (event: any, contexts?: any) => {
      logSnowplowEvent('trackSelfDescribingEvent (MOCK)', event, contexts);
      console.log(`[SNOWPLOW_TRACKING][${Platform.OS}] MOCK: Event NOT actually sent (web or failed init)`);
      return Promise.resolve();
    },
    setUserId: (userId: string | null) => {
      console.log(`[SNOWPLOW_TRACKING][${Platform.OS}] MOCK setUserId: ${userId || 'null'} (NOT actually set)`);
      return Promise.resolve();
    },
    trackPageView: (pageTitle: string) => {
      console.log(`[SNOWPLOW_TRACKING][${Platform.OS}] MOCK trackPageView: ${pageTitle} (NOT actually sent)`);
      return Promise.resolve();
    },
  };
  return mockTracker;
}

export async function initTracking({
  endpoint,
  namespace,
  appId,
  countryCode,
  forceReinitialize = false,
}: {
  endpoint: string;
  namespace: string;
  appId: string;
  countryCode?: string | null;
  forceReinitialize?: boolean;
}): Promise<any> {
  if (tracker && isInitialized) {
    if (!forceReinitialize) {
      //console.log(`[snowplow][${Platform.OS}] Tracker already initialized, returning existing instance`);
      return tracker;
    }

    console.log(`[snowplow][${Platform.OS}] ♻️ Force reinitializing tracker to apply latest configuration`);

    if (snowplowModule?.removeTracker && typeof snowplowModule.removeTracker === 'function') {
      try {
        const removalResult = snowplowModule.removeTracker(namespace);
        if (removalResult && typeof removalResult.then === 'function') {
          await removalResult;
        }
        console.log(`[snowplow][${Platform.OS}] 🧹 Previous tracker instance removed`);
      } catch (removalError) {
        console.warn(`[snowplow][${Platform.OS}] ⚠️ Failed to remove existing tracker instance:`, removalError);
      }
    }

    tracker = null;
    isInitialized = false;
    usingMockTracker = false;
    initAttemptCount = 0;
    initializedAppId = null;
    initializedCountryCode = null;
  }

  if (initAttemptCount >= MAX_INIT_ATTEMPTS) {
    console.warn(`[snowplow][${Platform.OS}] Max init attempts (${MAX_INIT_ATTEMPTS}) reached, returning mock tracker`);
    return getMockTracker();
  }

  try {
    // Replace "ww" with country code in appId if provided
    let finalAppId = appId;
    if (countryCode && appId.includes('_ww_')) {
      finalAppId = appId.replace('_ww_', `_${countryCode.toLowerCase()}_`);
    } else if (countryCode) {
      console.log(`[snowplow][${Platform.OS}] ℹ️ Country code provided (${countryCode}) but no "_ww_" pattern found in appId: ${appId}`);
    } else {
      console.log(`[snowplow][${Platform.OS}] ℹ️ No country code provided, using original appId: ${appId}`);
    }

    // Load platform-specific Snowplow module
    if (Platform.OS === 'web') {
      try {
        const browserTracker = require('@snowplow/browser-tracker');

        const newInstance = browserTracker.newTracker(namespace, endpoint, {
          appId: finalAppId || '',
          platform: 'web',
          cookieSameSite: 'None',
          contexts: {
            webPage: true,
          },
          stateStorageStrategy: 'cookieAndLocalStorage',
        });

        if (newInstance) {
          webTrackerInstance = newInstance;
        } else if (webTrackerInstance) {
          // newTracker ignores a duplicate namespace and returns nothing, so a
          // forced re-init (country code resolved after the first init) must
          // update the existing instance in place — otherwise the new appId is
          // silently dropped and events keep the "_ww_" appId.
          if (webTrackerInstance.core && typeof webTrackerInstance.core.setAppId === 'function') {
            webTrackerInstance.core.setAppId(finalAppId || '');
            console.log(`[snowplow][web] Reusing existing browser tracker, appId updated to: ${finalAppId}`);
          } else {
            console.warn('[snowplow][web] ⚠️ Existing browser tracker does not expose core.setAppId — appId change not applied');
          }
        } else {
          console.warn('[snowplow][web] ⚠️ newTracker returned no instance and no previous instance is known');
        }

        tracker = wrapTrackerWithLogging({
          trackSelfDescribingEvent: (event: any, contexts?: any) => {
            const contextArray = contexts?.context || contexts;
            browserTracker.trackSelfDescribingEvent({
              event: {
                schema: event.schema,
                data: event.data,
              },
              context: Array.isArray(contextArray) ? contextArray.map((c: any) => ({
                schema: c.schema,
                data: c.data,
              })) : undefined,
            });
          },
          setUserId: (userId: string | null) => {
            browserTracker.setUserId(userId ?? undefined);
          },
          // Built-in Snowplow page_view event (protocol type `pv`): lands in
          // the warehouse as event_name "page_view" with page_url/title —
          // there is no page_view self-describing schema in Iglu, so it must
          // not be sent as a trackSelfDescribingEvent.
          trackPageView: (pageTitle: string, contexts?: any) => {
            const contextArray = contexts?.context || contexts;
            browserTracker.trackPageView({
              title: pageTitle,
              context: Array.isArray(contextArray) ? contextArray.map((c: any) => ({
                schema: c.schema,
                data: c.data,
              })) : undefined,
            });
          },
        });

        isInitialized = true;
        initializedAppId = finalAppId;
        initializedCountryCode = countryCode || null;
        console.log(`[SNOWPLOW_TRACKING][web] Browser tracker initialized - AppId: ${finalAppId}, CountryCode: ${countryCode || 'N/A'}`);
        return tracker;
      } catch (browserError) {
        console.error('[snowplow][web] Failed to initialize browser tracker:', browserError);
        initAttemptCount++;
        usingMockTracker = true;
        tracker = getMockTracker();
        isInitialized = true;
        console.warn(`[snowplow][web] Using mock tracker (attempt ${initAttemptCount}/${MAX_INIT_ATTEMPTS})`);
        return tracker;
      }
    } else {
      try {
        snowplowModule = require('@snowplow/react-native-tracker');
        //console.log(`[snowplow][${Platform.OS}] React-native-tracker module loaded successfully`);
       // console.log(`[snowplow][${Platform.OS}] Module exports:`, Object.keys(snowplowModule));
      } catch (moduleError) {
        console.error(`[snowplow][${Platform.OS}] Failed to load react-native-tracker module:`, {
          error: moduleError,
          message: moduleError instanceof Error ? moduleError.message : String(moduleError),
          stack: moduleError instanceof Error ? moduleError.stack : undefined
        });
        console.warn(`[snowplow][${Platform.OS}] The @snowplow/react-native-tracker package may not be installed.`);
        console.warn(`[snowplow][${Platform.OS}] Run: npm install @snowplow/react-native-tracker`);
        initAttemptCount = MAX_INIT_ATTEMPTS;
        usingMockTracker = true;
        tracker = getMockTracker();
        isInitialized = true;
        return tracker;
      }

      try {
        if (!snowplowModule.newTracker) {
          throw new Error('newTracker method not found in @snowplow/react-native-tracker module');
        }

        tracker = await snowplowModule.newTracker({
          namespace,
          appId: finalAppId || '',
          endpoint: endpoint || '',
          eventMethod: 'post',
          protocol: 'https',
          postPath: '/com.snowplowanalytics.snowplow/tp2',
          sessionContext: true,
          platformContext: true,
          platformContextProperties: [],
          foregroundSessionTimeout: 600,
          backgroundSessionTimeout: 300,
          installAutotracking: true,
          lifecycleAutotracking: true,
          screenViewAutotracking: false,
          exceptionAutotracking: true,
          bufferSize: Platform.OS === 'android' ? 5 : 1,
          maxPostBytes: 52000,
          maxEventStoreSize: 1000,
          useAsyncStorageForEventStore: true,
          requestTimeout: 30000,
          retryStatusCodes: [408, 429, 500, 502, 503, 504],
          maxRetries: 3,
        });
        /*console.log(`[snowplow][${Platform.OS}] Native tracker created successfully (after await)`);
        console.log(`[snowplow][${Platform.OS}] Tracker type:`, typeof tracker);
        console.log(`[snowplow][${Platform.OS}] Tracker is Promise:`, tracker instanceof Promise);*/

        if (tracker && typeof tracker === 'object') {
          const allKeys = Object.keys(tracker);
          const methods = allKeys.filter(k => typeof tracker[k] === 'function');
          //console.log(`[snowplow][${Platform.OS}] All tracker keys:`, allKeys);
          //console.log(`[snowplow][${Platform.OS}] Tracker methods (${methods.length}):`, methods);

          if (methods.includes('trackSelfDescribingEvent')) {
            ;//console.log(`[snowplow][${Platform.OS}] ✅ trackSelfDescribingEvent method found`);
          } else {
            console.warn(`[snowplow][${Platform.OS}] ⚠️ trackSelfDescribingEvent method NOT found`);
            console.warn(`[snowplow][${Platform.OS}] This will cause SDK video tracking to fail`);
          }
        } else {
          console.warn(`[snowplow][${Platform.OS}] ⚠️ Tracker is not an object:`, tracker);
        }

        // Validate the tracker has all required methods
        const validation = validateTracker();
        if (!validation.valid) {
          console.error(`[snowplow][${Platform.OS}] ❌ Tracker validation failed: ${validation.error}`);
          if (validation.availableMethods) {
            ;//console.error(`[snowplow][${Platform.OS}] Available methods:`, validation.availableMethods);
          }
          //console.warn(`[snowplow][${Platform.OS}] SDK video tracking will not work properly`);
        } else {
          ;//console.log(`[snowplow][${Platform.OS}] ✅ Tracker validation passed`);
          //console.log(`[snowplow][${Platform.OS}] Available methods (${validation.availableMethods?.length}):`, validation.availableMethods);
        }

        const customUserAgent = buildSnowplowUserAgent();
        if (typeof tracker.setUseragent === 'function') {
          tracker.setUseragent(customUserAgent);
          console.log(`[snowplow][${Platform.OS}] User-agent set: ${customUserAgent}`);
        }

        // Wrap tracker with logging
        rawTracker = tracker;
        tracker = wrapTrackerWithLogging(tracker);
        console.log(`[SNOWPLOW_TRACKING][${Platform.OS}] Tracker wrapped with logging - all events will be logged`);
      } catch (trackerError) {
        console.error(`[snowplow][${Platform.OS}] CRITICAL: Failed to create tracker instance:`, {
          error: trackerError,
          message: trackerError instanceof Error ? trackerError.message : String(trackerError),
          stack: trackerError instanceof Error ? trackerError.stack : undefined
        });
        throw new Error(`Failed to create Snowplow tracker: ${trackerError instanceof Error ? trackerError.message : String(trackerError)}`);
      }
    }

    isInitialized = true;
    initializedAppId = finalAppId;
    initializedCountryCode = countryCode || null;
    console.log(`[SNOWPLOW_TRACKING][${Platform.OS}] Tracker initialized - AppId: ${finalAppId}, CountryCode: ${countryCode || 'N/A'}`);
    return tracker;
  } catch (error) {
    console.error(`[snowplow][${Platform.OS}] Failed to initialize tracker:`, {
      error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      endpoint,
      namespace,
      appId
    });
    initAttemptCount++;
    usingMockTracker = true;
    tracker = getMockTracker();
    isInitialized = true;
    console.warn(`[snowplow][${Platform.OS}] Using mock tracker (attempt ${initAttemptCount}/${MAX_INIT_ATTEMPTS})`);
    return tracker;
  }
}

export function requireTracker(): any {
  if (!tracker || !isInitialized) {
    throw new Error('Snowplow tracker not initialized. Call initTracking() first.');
  }
  return tracker;
}

export function isTrackerReady(): boolean {
  return isInitialized && tracker !== null && typeof tracker.trackSelfDescribingEvent === 'function';
}

export function isUsingMockTracker(): boolean {
  return usingMockTracker;
}

export function canRetryInit(): boolean {
  return initAttemptCount < MAX_INIT_ATTEMPTS;
}

export function resetForRetry(): void {
  if (initAttemptCount >= MAX_INIT_ATTEMPTS) return;
  tracker = null;
  rawTracker = null;
  isInitialized = false;
  usingMockTracker = false;
  initializedAppId = null;
  initializedCountryCode = null;
}

export function getCoreDiagnostics(): {
  isInitialized: boolean;
  usingMockTracker: boolean;
  initAttemptCount: number;
  maxAttempts: number;
  hasTracker: boolean;
  trackerReady: boolean;
  appId: string | null;
  countryCode: string | null;
} {
  return {
    isInitialized,
    usingMockTracker,
    initAttemptCount,
    maxAttempts: MAX_INIT_ATTEMPTS,
    hasTracker: tracker !== null,
    trackerReady: isTrackerReady(),
    appId: initializedAppId,
    countryCode: initializedCountryCode,
  };
}

export function getTrackerInfo(): { appId: string | null; countryCode: string | null; isInitialized: boolean } {
  return {
    appId: initializedAppId,
    countryCode: initializedCountryCode,
    isInitialized,
  };
}

export function verifyTrackerAppId(expectedCountryCode?: string | null): boolean {
  if (!isInitialized || !initializedAppId) {
    console.warn(`[snowplow][${Platform.OS}] ⚠️ Tracker not initialized, cannot verify appId`);
    return false;
  }

  if (expectedCountryCode && initializedCountryCode !== expectedCountryCode) {
    console.warn(`[snowplow][${Platform.OS}] ⚠️ Country code mismatch! Initialized: ${initializedCountryCode}, Expected: ${expectedCountryCode}`);
    console.warn(`[snowplow][${Platform.OS}] ⚠️ Tracker may be using wrong App ID`);
    return false;
  }

  if (expectedCountryCode && initializedAppId.includes('_ww_')) {
    console.warn(`[snowplow][${Platform.OS}] ⚠️ App ID still contains "_ww_": ${initializedAppId}`);
    console.warn(`[snowplow][${Platform.OS}] ⚠️ Country code transformation did not occur!`);
    return false;
  }

  return true;
}

export function validateTracker(): { valid: boolean; error?: string; availableMethods?: string[] } {
  if (!tracker) {
    return { valid: false, error: 'Tracker is null or undefined' };
  }

  if (typeof tracker !== 'object') {
    return { valid: false, error: `Tracker is not an object, got: ${typeof tracker}` };
  }

  const requiredMethods = ['trackSelfDescribingEvent', 'setUserId'];
  const missingMethods: string[] = [];
  const availableMethods = Object.keys(tracker).filter(k => typeof tracker[k] === 'function');

  for (const method of requiredMethods) {
    if (typeof tracker[method] !== 'function') {
      missingMethods.push(method);
    }
  }

  if (missingMethods.length > 0) {
    return {
      valid: false,
      error: `Tracker is missing required methods: ${missingMethods.join(', ')}`,
      availableMethods
    };
  }

  return { valid: true, availableMethods };
}
